import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Checkbox from '@material-ui/core/Checkbox'
import classNames from 'classnames'
import ListSubheader from '@material-ui/core/ListSubheader'
import Input from './input'
import { sort } from '../../../../utils'
import ExpandMore from '@material-ui/icons/ExpandMore'
import { isArray } from 'lodash'
import { Highlighted } from 'web/src/app/root/components/common/highlighted'
import { useSelector } from 'react-redux'

const useStyles = makeStyles(theme => ({
    root: {},
    selectRoot: {},
    select: {
        fontFamily: theme.fontFamily,
        color: theme.primary,
        fontSize: 14,
        height: '100%'
    },
    selectButton: {
        '&:focus': {
            background: 'none'
        },
        alignItems: 'center'
    },
    selectIcon: {
        color: theme.textSecondary
    },
    selectIconEmpty: {
        display: 'none'
    },
    menuPaper: {
        backgroundColor: theme.background,
        width: '400px'
    },
    menuWrapper: {
        maxHeight: '300px',
        overflowY: 'auto',
        padding: 0,
        backgroundColor: theme.background
    },
    option: {
        fontSize: 14,
        fontFamily: theme.fontFamily,
        color: theme.textSecondary,
        fontWeight: '400',
        padding: '4px 15px',
        '&:hover': {
            backgroundColor: `${theme.secondary} !important`
        }
    },
    optionSelected: {
        backgroundColor: `${theme.secondary} !important`,
        '&:hover': {
            backgroundColor: `${theme.secondary} !important`
        }
    },
    itemContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
    },
    itemId: {
        marginRight: 15
    },
    itemName: {},
    additionItems: {
        display: 'flex',
        alignItems: 'center'
    },
    error: {
        boxShadow: `0 0 5px ${theme.error}`
    },
    disabled: {
        // color: theme.buttonDisabled
    },
    checkbox: {
        margin: 0,
        padding: 0
    },
    headerRoot: {
        width: '100%',
        height: '100%',
        display: 'flex',
        padding: '5px'
    },
    headerMenuItemRoot: {
        padding: 0,
        outline: 'none',
        background: theme.background
    },
    searchInputRoot: {
        display: 'block'
    },
    headerIconButton: {
        margin: '0',
        padding: '7px'
    },
    headerSortIcon: {
        margin: '0 0 0 5px',
        color: theme.textPrimary
    },
    searchInputPaper: {
        padding: '5px 10px'
    },
    searchInputFromControl: {
        height: '38px'
    },
    itemInner: {
        marginRight: 10,
        display: 'flex',
        alignItems: 'center'
    },
    renderedValue: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginRight: 10
    }
}))

function Picker(props) {
    const orderByTypes = {
        asc: 'asc',
        des: 'des'
    }
    const { options, withSearchAndSort, isSortingDisabled } = props
    const classes = useStyles()
    const [searchValue, setSearchValue] = useState('')
    const [optionsOrderBy, setOptionsOrderBy] = useState(orderByTypes.des)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const { locale } = useSelector(state => state.main)

    let parseOptions = options => {
        if (isArray(options)) {
            return options
        }
        return Object.keys(options).map(key => ({ key, name: options[key] }))
    }

    const [parsedOptions, setParsedOptions] = useState(() =>
        parseOptions(options)
    )

    useEffect(() => {
        const newParsedOptions = parseOptions(options)
        if (!isSortingDisabled) {
            let sortedOptions = newParsedOptions.sort((a, b) => {
                return a.name.localeCompare(b.name)
            })
            setParsedOptions(sortedOptions)
        } else {
            setParsedOptions(newParsedOptions)
        }

        if (withSearchAndSort) {
            setSearchValue('')
        }
    }, [options])

    useEffect(() => {
        let filteredAndSortedOptions = parseOptions(options).filter(
            ({ name }) =>
                String(name).toLowerCase().includes(searchValue.toLowerCase())
        )

        if (!isSortingDisabled) {
            filteredAndSortedOptions.sort((a, b) =>
                a.name.localeCompare(b.name)
            )
        }

        setParsedOptions(filteredAndSortedOptions)
    }, [searchValue])

    useEffect(() => {
        if (!isSortingDisabled) {
            const isAscSort = optionsOrderBy === orderByTypes.asc
            setParsedOptions(currentParsedOptions =>
                sort(currentParsedOptions, 'name', 'string', isAscSort)
            )
        }
    }, [optionsOrderBy])

    const onOptionChange = e => props.onChange(e.target.value)

    const onMenuExited = () => {
        setIsMenuOpen(false)

        setParsedOptions(parseOptions(options))
        if (withSearchAndSort) {
            setSearchValue('')
            setOptionsOrderBy(orderByTypes.des)
        }
    }

    const {
        withId,
        isWithEmptyValue,
        emptyValue,
        isWithEmptyValueText,
        value,
        multiple,
        onChange,
        noSelectIcon,
        additionalItems,
        firstIcon,
        firstIconAction,
        renderValue,
        alwaysRenderEmptyValueText,
        isError,
        disabled,
        defaultDisabled,
        localeStore,
        disablePortal,
        ...restProps
    } = props
    const restClasses = { ...restProps }.className

    return (
        <FormControl
            className={classNames({
                [classes.root]: true,
                [`${classes.formControl}`]: true,
                [`${classes.error}`]: isError,
                [`${restClasses}`]: restClasses
            })}>
            {!!defaultDisabled && (
                <span className={classes.disabled}>{defaultDisabled}</span>
            )}
            {!defaultDisabled && (
                <Select
                    onOpen={() => {
                        setIsMenuOpen(true)
                    }}
                    disabled={disabled}
                    displayEmpty={
                        isWithEmptyValue || alwaysRenderEmptyValueText
                    }
                    disableUnderline={true}
                    value={value}
                    multiple={multiple}
                    onChange={onOptionChange}
                    className={classes.select}
                    classes={{
                        root: classes.selectRoot,
                        select: classes.selectButton,
                        selectMenu: classes.selectMenu,
                        icon: noSelectIcon
                            ? classes.selectIconEmpty
                            : classes.selectIcon
                    }}
                    MenuProps={{
                        autoFocus: true,
                        getContentAnchorEl: null,
                        disablePortal: disablePortal,
                        onExited: onMenuExited,
                        classes: {
                            paper: classes.menuPaper
                        },
                        MenuListProps: {
                            classes: {
                                root: classes.menuWrapper
                            }
                        }
                    }}
                    renderValue={
                        renderValue &&
                        (id => (
                            <div className={classes.renderedValue}>
                                {renderValue(
                                    id && !(Array.isArray(id) && !id.length)
                                        ? { id, name: options[id] }
                                        : isWithEmptyValueText
                                )}
                            </div>
                        ))
                    }>
                    {withSearchAndSort && (
                        <ListSubheader
                            disableGutters={true}
                            className={classes.headerMenuItemRoot}>
                            <div
                                className={classes.headerRoot}
                                onClick={ev => ev.stopPropagation()}>
                                <Input
                                    autoFocus={isMenuOpen && withSearchAndSort}
                                    onKeyDown={e => e.stopPropagation()}
                                    classes={{
                                        inputBase: classes.searchInputRoot,
                                        inputPaper: classes.searchInputPaper,
                                        formControl:
                                            classes.searchInputFromControl
                                    }}
                                    value={searchValue}
                                    setValue={setSearchValue}
                                    placeholder={'Search'}
                                />
                            </div>
                        </ListSubheader>
                    )}
                    {isWithEmptyValue && (
                        <MenuItem
                            onKeyDown={e => e.stopPropagation()}
                            value={emptyValue}
                            className={classes.option}
                            classes={{
                                selected: classes.optionSelected
                            }}>
                            <div className={classes.itemContainer}>
                                {isWithEmptyValueText || <em>--</em>}
                            </div>
                        </MenuItem>
                    )}
                    {!Array.isArray(parsedOptions) ? (
                        <MenuItem
                            className={classes.option}
                            classes={{
                                selected: classes.optionSelected
                            }}>
                            <div className={classes.itemContainer}>
                                {locale.common.nothingWasFound}
                            </div>
                        </MenuItem>
                    ) : (
                        parsedOptions.map(
                            ({ key, name, depth, hasChildren }) => {
                                return (
                                    <MenuItem
                                        onKeyDown={e => e.stopPropagation()}
                                        className={classes.option}
                                        classes={{
                                            selected: classes.optionSelected
                                        }}
                                        key={key}
                                        value={key}
                                        style={{
                                            paddingLeft: depth
                                                ? !hasChildren
                                                    ? depth * 15
                                                    : depth * 10
                                                : 0
                                        }}>
                                        <div className={classes.itemContainer}>
                                            <div
                                                className={classNames(
                                                    classes.itemInner,
                                                    classes.textEllipsis
                                                )}>
                                                {withId && (
                                                    <span
                                                        className={
                                                            classes.itemId
                                                        }>
                                                        {key}
                                                    </span>
                                                )}

                                                {hasChildren && (
                                                    <ExpandMore
                                                        style={{
                                                            display:
                                                                value === key &&
                                                                !isMenuOpen
                                                                    ? 'none'
                                                                    : 'block'
                                                        }}
                                                        fontSize={'inherit'}
                                                    />
                                                )}

                                                <Highlighted
                                                    text={
                                                        <span
                                                            className={
                                                                classes.option
                                                            }>
                                                            {name}
                                                        </span>
                                                    }
                                                    highlight={searchValue}
                                                />
                                            </div>
                                            <div
                                                className={
                                                    classes.additionItems
                                                }>
                                                {additionalItems &&
                                                    additionalItems.map(item =>
                                                        item.icon({
                                                            key,
                                                            onClick:
                                                                item.action.bind(
                                                                    this,
                                                                    true,
                                                                    {
                                                                        id: key,
                                                                        name
                                                                    }
                                                                )
                                                        })
                                                    )}
                                            </div>
                                        </div>
                                        {multiple && (
                                            <Checkbox
                                                checked={value.includes(key)}
                                                className={classes.checkbox}
                                            />
                                        )}
                                    </MenuItem>
                                )
                            }
                        )
                    )}
                </Select>
            )}
        </FormControl>
    )
}

export default Picker
